import { readableColor } from 'polished';
import {
  componentTypeDefaultShape,
  ConditionalFormattingState,
} from '@ardoq/data-model';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import type { Bounds, Rectangle, Vector } from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import { colors } from '@ardoq/design-tokens';
import { currentTimestamp } from '@ardoq/date-time';
import { svgImage } from 'tabview/canvasRendering/canvasResolvedImages';
import {
  BLOCKS_VIEW_NODE_PAD,
  BLOCKS_VIEW_LEAF_RD_OUTERSIZE,
  BLOCKS_VIEW_LEAF_BACKGROUNDCOLOR,
  BLOCKS_VIEW_LEAF_BORDERCOLOR,
  BLOCKS_VIEW_NODE_MARGIN,
  BLOCKS_VIEW_NODE_BORDERRADIUS,
  BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT,
  BLOCKS_VIEW_NODE_LABEL_FONT,
  BLOCKS_VIEW_CONTEXT_COLOR,
  BLOCKS_VIEW_NODE_SEP,
  BLOCKS_VIEW_LEAF_RD_INNERSIZE,
  BLOCKS_VIEW_LEAF_BORDERWIDTH,
  BLOCKS_VIEW_NODE_LABEL_WRAP as BLOCKS_VIEW_GROUP_LABELWRAP,
  BLOCKS_VIEW_GROUP_BACKGROUNDCOLOR,
  BLOCKS_VIEW_GROUP_BORDERCOLOR,
  BLOCKS_VIEW_GROUP_BORDERWIDTH,
  BLOCKS_VIEW_CONTEXT_WIDTH,
  BLOCKS_VIEW_MULTILABEL_FONT,
  BLOCKS_VIEW_NODE_SUBLABEL_FONT,
  BLOCKS_VIEW_NODE_MINIMUM_WIDTH,
  BLOCKS_VIEW_NODE_MINIMUM_HEIGHT,
  BLOCKS_VIEW_COLLAPSED_GROUP_MINIMUM_HEIGHT,
  BLOCKS_VIEW_MULTILABEL_BORDERRADIUS,
  BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT,
  BLOCKS_VIEW_MULTILABEL_PADDING_VERTICAL,
  BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL,
  BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT,
  BLOCKS_VIEW_HEADER_INNER_HEIGHT,
  BLOCKS_VIEW_GROUP_BADGE_FONT_SIZE,
  BLOCKS_VIEW_EXPANDER_MARGIN_RIGHT,
  BLOCKS_VIEW_LEAF_LABEL_MARGIN,
  BLOCKS_VIEW_LEAF_ICON_SIZE,
} from '../../consts';
import { type BlocksViewNode, BlocksViewVisualState } from '../../types';
import { CanvasRenderingContext } from '../types';
import { isGroup } from '../../viewModel$/util';
import {
  rectBloat,
  rectCenter,
  rectContains,
  rectHeight,
  rectWidth,
  vectorMag,
} from '../../misc/geometry';
import { sum, sumBy } from 'lodash';
import { wrapString } from '../../misc/string';
import { readableButtonColor } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/utils';
import { determineStyleSize } from 'yfilesExtensions/styles/util';
import { urlIconRect } from './urlIconRect';
import {
  collapsedGroupHeaderBottom,
  measureNodeLabel,
  openGroupHeaderTopTextBaseline,
} from '../util';
import { descendantCountBadgeRect } from './descendantCountBadgeRect';
import { drawDescendantCountBadge } from 'tabview/canvasRendering/descendantCountBadge/drawDescendantCountBadge';
import { drawCollapsedNonComponentGroup } from './drawCollapsedNonComponentGroup';
import { fillAndStrokeWithShadowLayers, shadowLayers } from 'tabview/utils';
import { drawExpander } from './drawExpander';
import { drawUrlIcon } from './drawUrlIcon';
import { groupExpanderRect } from './groupExpanderRect';
import { nodeDisplayLabel } from './nodeDisplayLabel';
import { labelRect } from './labelRect';
import { paintImageInCircle } from 'tabview/canvasRendering/paintImageInCircle';
import { drawImage } from 'tabview/canvasRendering/drawImage';
import { paintIcon } from 'tabview/canvasRendering/paintIcon';

const isSyntheticGroup = (node: BlocksViewNode) => {
  return isGroup(node) && !node.parent;
};

// this is a (typically) fairly pessimistic way of measuring a string width.
// although it might get it wrong sometimes it has the advantage of being fast
// and not requiring a canvas rendering context. adding a canvas rendering context
// is probably a better long-term solution but may require some modifications in
// the layout code which are probably better left until the constraint solver
// is updated to become 2D (which is pretty much required before a proper release)
const approxMeasure = (a: string) =>
  a.length * 0.6 * BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT;

const measureOtherLabel = canvasLabelMeasurer(BLOCKS_VIEW_MULTILABEL_FONT);
const approxLabelWidth = (label: string[]) => {
  let ret = 0;

  for (const line of label) {
    ret = Math.max(ret, approxMeasure(line));
  }

  return ret;
};

const exactLabelWidth = (label: string[], ctx: CanvasRenderingContext) => {
  let ret = 0;

  for (const line of label) {
    ret = Math.max(ret, ctx.measureText(line).width);
  }

  return ret;
};

const getLabelHeight = (label: string[], fontSize: number) => {
  return label.length * fontSize;
};

const otherLabelsHeight = (multiLabels: string[][]) =>
  sumBy(
    multiLabels,
    multiLabel =>
      2 * BLOCKS_VIEW_MULTILABEL_PADDING_VERTICAL +
      multiLabel.length * BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT
  );
const getMultilabelsHeight = ({
  displayLabel: [, , ...multiLabels],
}: BlocksViewNode) => otherLabelsHeight(multiLabels);

const getNodeMinimumCellWidth = (node: BlocksViewNode) => {
  if (isSyntheticGroup(node)) {
    return (
      BLOCKS_VIEW_NODE_MARGIN + sum(node.colSize) + BLOCKS_VIEW_NODE_MARGIN
    );
  }

  if (!isGroup(node) || !node.open) {
    return BLOCKS_VIEW_NODE_MINIMUM_WIDTH;
  }

  /* expander, sep, rd, sep, label */

  const maxTitleWidth = BLOCKS_VIEW_GROUP_LABELWRAP;
  const label = wrapString(
    node.labels[0],
    BLOCKS_VIEW_GROUP_LABELWRAP,
    approxMeasure
  );
  const widthHeader =
    BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT +
    BLOCKS_VIEW_NODE_SEP +
    BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT +
    BLOCKS_VIEW_NODE_SEP +
    approxLabelWidth(label);

  /* sublabel */

  const sublabel = wrapString(node.labels[1], maxTitleWidth, approxMeasure);
  const widthSublabel = approxLabelWidth(sublabel);

  /* multilabel */

  let widthMultilabel = 0;
  for (let i = 2; i < node.labels.length; ++i) {
    const wrapped = wrapString(node.labels[i], maxTitleWidth, approxMeasure);
    const width =
      BLOCKS_VIEW_NODE_SEP +
      wrapped.reduce((max, line) => Math.max(max, approxMeasure(line)), 0) +
      1 +
      BLOCKS_VIEW_NODE_SEP;

    widthMultilabel = Math.max(widthMultilabel, width);
  }

  /* columns */

  const widthColumns = sum(node.colSize);

  /* get the minimum width */

  const width =
    BLOCKS_VIEW_NODE_MARGIN +
    BLOCKS_VIEW_NODE_PAD +
    Math.max(widthHeader, widthSublabel, widthMultilabel, widthColumns) +
    BLOCKS_VIEW_NODE_PAD +
    BLOCKS_VIEW_NODE_MARGIN;

  return Math.max(BLOCKS_VIEW_NODE_MINIMUM_WIDTH, width);
};

const COLLAPSED_GROUP_HEADER_HEIGHT =
  BLOCKS_VIEW_HEADER_INNER_HEIGHT + 2 * BLOCKS_VIEW_NODE_PAD;
const COLLAPSED_NON_COMPONENT_GROUP_MINIMUM_HEIGHT =
  BLOCKS_VIEW_HEADER_INNER_HEIGHT + 2 * BLOCKS_VIEW_NODE_PAD;

const getNodeMinimumCellHeight = (node: BlocksViewNode, width: number) => {
  const getMultilabelHeight = (space: number) => {
    // this should take into account that I have a bit less space
    const {
      labels: [, , ...multiLabels],
    } = node;
    if (!multiLabels.length) {
      return 0;
    }
    const wrappedLabels = multiLabels.map(label =>
      wrapString(label, space, measureOtherLabel)
    );
    return otherLabelsHeight(wrappedLabels);
  };

  if (isSyntheticGroup(node)) {
    return (
      BLOCKS_VIEW_NODE_MARGIN + sum(node.rowSize) + BLOCKS_VIEW_NODE_MARGIN
    );
  }

  /* find out how much horizontal space we have */
  const {
    open,
    labels: [mainLabel],
  } = node;
  const isGroupNode = isGroup(node);
  const space =
    width -
    (BLOCKS_VIEW_NODE_MARGIN +
      BLOCKS_VIEW_NODE_PAD +
      BLOCKS_VIEW_NODE_PAD +
      BLOCKS_VIEW_NODE_MARGIN);

  if (!isGroupNode) {
    const labelSpace = space - 2 * BLOCKS_VIEW_LEAF_LABEL_MARGIN;
    const heightLabel =
      BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT *
      wrapString(mainLabel, labelSpace, measureNodeLabel).length;
    const heightMultiLabel = getMultilabelHeight(
      labelSpace - 2 * BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL
    );
    const height =
      BLOCKS_VIEW_NODE_MARGIN +
      BLOCKS_VIEW_NODE_PAD +
      BLOCKS_VIEW_LEAF_RD_INNERSIZE +
      (heightLabel ? SMALL_SEP + heightLabel : 0) +
      (heightMultiLabel ? SMALL_SEP + heightMultiLabel : 0) +
      BLOCKS_VIEW_NODE_PAD +
      BLOCKS_VIEW_NODE_MARGIN;
    return Math.max(BLOCKS_VIEW_NODE_MINIMUM_HEIGHT, height);
  }

  const labelSpace =
    space - 2 * (BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT + BLOCKS_VIEW_NODE_SEP);

  const heightLabel =
    BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT *
    wrapString(node.labels[0], labelSpace, approxMeasure).length;

  const heightMultilabel = getMultilabelHeight(
    space - BLOCKS_VIEW_NODE_SEP - BLOCKS_VIEW_NODE_SEP
  );

  if (!open) {
    const isComponent = node.isComponent;
    if (!isComponent) {
      // #region collapsed non-component group
      const leftWidth =
        BLOCKS_VIEW_HEADER_INNER_HEIGHT - BLOCKS_VIEW_EXPANDER_MARGIN_RIGHT;
      const rightWidth = rectWidth(descendantCountBadgeRect(node));
      const labelSpace = space - leftWidth - rightWidth;
      const wrappedLabel = wrapString(mainLabel, labelSpace, measureNodeLabel);
      const labelHeight =
        BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT * wrappedLabel.length;

      return (
        Math.max(
          labelHeight + 2 * BLOCKS_VIEW_NODE_PAD,
          COLLAPSED_NON_COMPONENT_GROUP_MINIMUM_HEIGHT
        ) +
        2 * BLOCKS_VIEW_NODE_MARGIN
      );
      // #endregion
    }
    const height =
      COLLAPSED_GROUP_HEADER_HEIGHT +
      BLOCKS_VIEW_NODE_MARGIN +
      BLOCKS_VIEW_NODE_PAD +
      BLOCKS_VIEW_LEAF_RD_OUTERSIZE +
      BLOCKS_VIEW_NODE_SEP +
      (heightLabel !== 0 ? heightLabel + BLOCKS_VIEW_NODE_SEP : 0) +
      (heightMultilabel !== 0 ? heightMultilabel + BLOCKS_VIEW_NODE_SEP : 0) +
      BLOCKS_VIEW_NODE_PAD +
      BLOCKS_VIEW_NODE_MARGIN;

    return Math.max(BLOCKS_VIEW_COLLAPSED_GROUP_MINIMUM_HEIGHT, height);
  }

  const heightSublabel =
    BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT *
    wrapString(node.labels[1], space, approxMeasure).length;
  const heightRows = node.rowSize ? sum(node.rowSize) : 0;

  const height =
    BLOCKS_VIEW_NODE_MARGIN +
    BLOCKS_VIEW_NODE_PAD +
    Math.max(
      BLOCKS_VIEW_HEADER_INNER_HEIGHT + SMALL_SEP,
      heightLabel + BLOCKS_VIEW_NODE_SEP
    ) +
    (heightSublabel !== 0 ? heightSublabel + BLOCKS_VIEW_NODE_SEP : 0) +
    (heightMultilabel !== 0 ? heightMultilabel + BLOCKS_VIEW_NODE_SEP : 0) +
    BLOCKS_VIEW_NODE_SEP +
    heightRows +
    BLOCKS_VIEW_NODE_PAD +
    BLOCKS_VIEW_NODE_MARGIN;

  return Math.max(BLOCKS_VIEW_NODE_MINIMUM_HEIGHT, height);
};

export const getNodeMinimumCellSize = (node: BlocksViewNode): Vector => {
  // this is used in for simultaneous constraint solving, but would
  // be replaced by separate calls for orthogonal constraint solving

  const w = getNodeMinimumCellWidth(node);
  const h = getNodeMinimumCellHeight(node, w - 1); // patch the numerous off-by-ones. TODO don't have the off-by-ones in the first place

  return [w, h];
};

const SMALL_SEP = 4;
const IMAGE_OUTLINE_WIDTH = 2.5;
const IMAGE_MARGIN = 8;

export const setNodeCellRect = (node: BlocksViewNode, cell: Bounds) => {
  if (node.cell) {
    node.animation = {
      begin: currentTimestamp(),
      duration: 500,
      from: node.cell,
    };
  }

  node.cell = [...cell];
  node.bounds = rectBloat(node.cell, -BLOCKS_VIEW_NODE_MARGIN);
  node.content = undefined;
  node.displayLabel = [];

  if (isSyntheticGroup(node)) {
    node.content = [...node.bounds];
    return;
  }

  node.displayLabel = nodeDisplayLabel(node); // wrap strings to their final resting place

  const labelHeight = getLabelHeight(
    node.displayLabel[0],
    BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT
  );
  const isOpenGroup = isGroup(node) && Boolean(node.open);
  const headerHeight = isOpenGroup
    ? Math.max(
        BLOCKS_VIEW_HEADER_INNER_HEIGHT + SMALL_SEP,
        labelHeight + BLOCKS_VIEW_NODE_SEP
      )
    : labelHeight + BLOCKS_VIEW_NODE_SEP;

  const y =
    node.bounds[1] +
    BLOCKS_VIEW_NODE_PAD +
    headerHeight +
    getMultilabelsHeight(node);

  node.content = [
    node.bounds[0] + BLOCKS_VIEW_NODE_PAD,
    y,
    node.bounds[2] - BLOCKS_VIEW_NODE_PAD,
    node.bounds[3] - BLOCKS_VIEW_NODE_PAD,
  ];
};

export const hitTestNode = (node: BlocksViewNode, pos: Vector) => {
  if (
    isGroup(node) &&
    hitTestNodePosition(node, pos) === HitTestResult.EXPANDER
  ) {
    return true;
  }

  if (!node.cell || !rectContains(node.cell, pos)) {
    /* cell rect doesn't contain point */
    return false;
  }

  return rectContains(node.bounds, pos);
};

export enum HitTestResult {
  NONE,
  HIT,
  EXPANDER,
  LINK_ICON,
}

const circleHit = (rect: Bounds, [x, y]: Vector) => {
  const [cx, cy] = rectCenter(rect);
  const r = 0.5 * Math.min(rectWidth(rect), rectHeight(rect));

  return vectorMag([x - cx, y - cy]) <= r;
};
export const hitTestNodePosition = (
  node: BlocksViewNode,
  pos: Vector
): HitTestResult => {
  const { cell } = node;
  if (!cell) {
    return HitTestResult.NONE;
  }
  const isGroupNode = isGroup(node);
  if (isGroupNode) {
    if (circleHit(groupExpanderRect(node), pos)) {
      return HitTestResult.EXPANDER;
    }
    if (rectContains(urlIconRect(node), pos)) {
      return HitTestResult.LINK_ICON;
    }
    return HitTestResult.NONE;
  }

  if (!rectContains(cell, pos)) {
    return HitTestResult.NONE;
  }

  if (rectContains(urlIconRect(node), pos)) {
    return HitTestResult.LINK_ICON;
  }

  return HitTestResult.HIT;
};

const getNodeBounds = (_node: BlocksViewNode, cell: Bounds): Bounds => {
  return BLOCKS_VIEW_NODE_MARGIN
    ? rectBloat(cell, -BLOCKS_VIEW_NODE_MARGIN)
    : cell;
};

let ctx: CanvasRenderingContext | null = null; // this is global rendering context
let pixel: number = 0; // pixel gets used all over the fucking place too

const getNodeColor = (node: BlocksViewNode) => node.color ?? 'transparent';

const roundRect = (rc: Bounds, radius: number | (number | DOMPointInit)[]) => {
  ctx!.roundRect(rc[0], rc[1], rectWidth(rc), rectHeight(rc), radius);
};

const drawShape = (
  ctx: CanvasRenderingContext,
  color: string,
  shapeName: string,
  [x, y, width, height]: Rectangle
) =>
  ctx.drawImage(
    svgImage(`${shapeName}~~${color}~~${color}~~${color}~~${width}~~${height}`),
    x,
    y
  );

/** @todo not implemented for open groups or collapsed non-component groups. */
const contentCenter = (
  { children, open }: BlocksViewNode,
  cell: Bounds
): Point => {
  if (!cell) {
    return [NaN, NaN];
  }
  const isGroup = children?.length;
  if (isGroup && open) {
    return [NaN, NaN];
  }
  if (!isGroup) {
    return rectCenter(cell);
  }
  const [left, , right, bottom] = cell;
  const headerBottom = collapsedGroupHeaderBottom(cell);
  return [
    left + (right - left) / 2,
    headerBottom + (bottom - headerBottom) / 2,
  ];
};

const drawHoverHighlight = (ctx: CanvasRenderingContext, bounds: Bounds) => {
  ctx.beginPath();
  roundRect(bounds, BLOCKS_VIEW_NODE_BORDERRADIUS);
  ctx.fillStyle = '#141414';
  const globalAlpha = ctx.globalAlpha;
  ctx.globalAlpha = 0.05;
  ctx.fill();
  ctx.globalAlpha = globalAlpha;
};

export const drawNode = (
  node: BlocksViewNode,
  cell: Bounds,
  alpha: number,
  context: CanvasRenderingContext
) => {
  if (!node.parent) {
    return;
  }

  ctx = context;
  pixel = 1.0 / context.getTransform().a;

  const isHighlight = node.visualState & BlocksViewVisualState.Highlight;
  const isHover = node.visualState & BlocksViewVisualState.Hover;
  const isSelect = node.visualState & BlocksViewVisualState.Select;
  const nodeColor = getNodeColor(node);
  const bg = isGroup(node)
    ? BLOCKS_VIEW_GROUP_BACKGROUNDCOLOR
    : BLOCKS_VIEW_LEAF_BACKGROUNDCOLOR;
  const nodeIsGroup = isGroup(node);
  const borderColor = nodeIsGroup
    ? BLOCKS_VIEW_GROUP_BORDERCOLOR
    : BLOCKS_VIEW_LEAF_BORDERCOLOR;
  const lineWidth =
    pixel *
    (isHover ? 1.5 : 1.0) *
    (nodeIsGroup
      ? BLOCKS_VIEW_GROUP_BORDERWIDTH
      : BLOCKS_VIEW_LEAF_BORDERWIDTH);

  const bounds = getNodeBounds(node, cell);
  const body = rectBloat(bounds, -BLOCKS_VIEW_NODE_PAD);

  const multilabelHeight = getMultilabelsHeight(node);
  const globalAlpha = isHighlight ? 1 : alpha;
  ctx.globalAlpha = globalAlpha;

  const [mainLabel, subLabel, ...multiLabels] = node.displayLabel;

  const drawRepresentationData = (center: Vector, diameter: number) => {
    const { representationData: rd, conditionalFormattingState, open } = node;
    if (!rd) {
      return;
    }
    const { icon } = rd;
    const radius = 0.5 * diameter;
    const isOpenGroup = nodeIsGroup && open;
    const isImage = rd.isImage || (isOpenGroup && !icon);
    if (isImage) {
      const isDimConditionalFormating =
        conditionalFormattingState === ConditionalFormattingState.DIM;
      ctx!.globalAlpha = isDimConditionalFormating ? 0.32 : 1;
      const [cx, cy] = center;
      if (isOpenGroup) {
        drawImage(ctx!, rd.value!, cx, cy, diameter);
      } else {
        paintImageInCircle(
          ctx!,
          rd.value!,
          cx,
          cy,
          radius,
          diameter - 2 * IMAGE_MARGIN
        );
      }

      ctx!.globalAlpha = globalAlpha;
      return;
    }

    if (icon) {
      const [cx, cy] = center;
      const iconColor = readableColor(
        nodeColor,
        colors.white950,
        colors.grey950
      );
      const iconSize = isOpenGroup ? diameter : BLOCKS_VIEW_LEAF_ICON_SIZE;
      if (!isOpenGroup) {
        ctx!.fillStyle = nodeColor;
        ctx!.beginPath();
        ctx!.ellipse(cx, cy, radius, radius, 0, 0, 2 * Math.PI);
        ctx!.fill();
      }
      paintIcon(ctx!, cx, cy, iconSize, iconColor, icon);
    }
  };
  const { multiLabelStyle } = node;
  /** @returns the position under the bottom margin of the last label. */
  const drawMultilabels = (
    left: number,
    top: number,
    right: number,
    center: boolean
  ) => {
    ctx = ctx!;

    const leftAligned = (
      left: number,
      top: number,
      _right: number,
      label: string[]
    ) => {
      const w = exactLabelWidth(label, ctx!);
      const h = label.length * BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT;
      const rc: Bounds = [
        left,
        top,
        left +
          BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL +
          w +
          BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL,
        top + h,
      ];

      const buildPath = () => {
        ctx!.beginPath();
        roundRect(rc, BLOCKS_VIEW_MULTILABEL_BORDERRADIUS);
      };

      fillAndStrokeWithShadowLayers(
        ctx!,
        shadowLayers,
        nodeIsGroup
          ? multiLabelStyle.groupFillStyle
          : multiLabelStyle.fillStyle,
        multiLabelStyle.strokeStyle,
        buildPath
      );

      ctx!.fillStyle = multiLabelStyle.textColor;

      ctx!.textAlign = 'left';
      ctx!.textBaseline = 'middle';

      for (let i = 0; i < label.length; ++i) {
        const y = rc[1] + (i + 0.5) * BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT;
        ctx!.fillText(
          label[i],
          rc[0] + BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL,
          y
        );
      }

      return h;
    };

    const centered = (
      left: number,
      top: number,
      right: number,
      label: string[]
    ) => {
      const w = exactLabelWidth(label, ctx!);
      const h = label.length * BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT;

      const rc: Bounds = [
        0.5 * (left + right - w) - BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL,
        top,
        0.5 * (left + right + w) + BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL,
        top + h,
      ];
      const cx = 0.5 * (rc[0] + rc[2]);

      const buildPath = () => {
        ctx!.beginPath();
        roundRect(rc, BLOCKS_VIEW_MULTILABEL_BORDERRADIUS);
      };

      fillAndStrokeWithShadowLayers(
        ctx!,
        shadowLayers,
        nodeIsGroup
          ? multiLabelStyle.groupFillStyle
          : multiLabelStyle.fillStyle,
        multiLabelStyle.strokeStyle,
        buildPath
      );

      ctx!.fillStyle = multiLabelStyle.textColor;
      ctx!.textAlign = 'center';
      ctx!.textBaseline = 'middle';

      for (let i = 0; i < label.length; ++i) {
        const y = rc[1] + (i + 0.5) * BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT;
        ctx!.fillText(label[i], cx, y);
      }

      return h;
    };

    ctx.font = BLOCKS_VIEW_MULTILABEL_FONT;

    let y = top;

    for (const label of multiLabels) {
      const h = center
        ? centered(left, y, right, label)
        : leftAligned(left, y, right, label);

      y += h + SMALL_SEP;
    }
    return y;
  };

  if (isSelect) {
    const BLOCKS_VIEW_SELECT_WIDTH = 1.5;
    const rect = rectBloat(bounds, 0.5 * BLOCKS_VIEW_SELECT_WIDTH * pixel);

    ctx.beginPath();
    roundRect(rect, BLOCKS_VIEW_NODE_BORDERRADIUS);
    ctx.lineWidth = BLOCKS_VIEW_SELECT_WIDTH * pixel;
    ctx.strokeStyle = 'black';
    ctx.stroke();
  }

  if (node.isContext) {
    const contextStrokeWidth = BLOCKS_VIEW_CONTEXT_WIDTH * pixel;
    const rect = rectBloat(bounds, contextStrokeWidth);
    const borderRadius = BLOCKS_VIEW_NODE_BORDERRADIUS + contextStrokeWidth;
    ctx.beginPath();
    roundRect(rect, borderRadius);
    ctx.lineWidth = contextStrokeWidth;
    ctx.strokeStyle = BLOCKS_VIEW_CONTEXT_COLOR;
    ctx.stroke();
  }

  const { open } = node;
  const isComponent = node.isComponent;
  const isOpenGroup = nodeIsGroup && open;
  if (isOpenGroup) {
    // #region draw group bounds and background

    // #region header part in node color
    const stripeBottom =
      bounds[1] +
      BLOCKS_VIEW_NODE_PAD +
      Math.max(
        BLOCKS_VIEW_HEADER_INNER_HEIGHT + SMALL_SEP,
        getLabelHeight(mainLabel, BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT)
      );
    const stripeColor = isComponent ? nodeColor : bg;

    ctx.beginPath();
    roundRect(
      [bounds[0], bounds[1], bounds[2], stripeBottom],
      [BLOCKS_VIEW_NODE_BORDERRADIUS, BLOCKS_VIEW_NODE_BORDERRADIUS, 0, 0]
    );
    ctx.fillStyle = stripeColor;
    ctx.fill();
    // #endregion

    // #region a line at the bottom of the header stripe
    const [x1, , x2] = bounds;
    ctx.moveTo(x1, stripeBottom);
    ctx.strokeStyle = BLOCKS_VIEW_LEAF_BORDERCOLOR;
    ctx.lineTo(x2, stripeBottom);
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    // #endregion

    // #region background part in node background
    ctx.beginPath();
    roundRect(
      [bounds[0], stripeBottom, bounds[2], bounds[3]],
      [0, 0, BLOCKS_VIEW_NODE_BORDERRADIUS, BLOCKS_VIEW_NODE_BORDERRADIUS]
    );
    ctx.fillStyle = bg;
    ctx.fill();
    // #endregion

    // #region border in node border
    ctx.beginPath();
    roundRect(
      [bounds[0], bounds[1], bounds[2], bounds[3]],
      BLOCKS_VIEW_NODE_BORDERRADIUS
    );
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    // #endregion

    // #endregion

    /* now draw what's inside */

    let left = body[0];
    let right = body[2];

    {
      const sz = BLOCKS_VIEW_HEADER_INNER_HEIGHT;
      right = left + sz;
      const expanderColor = readableButtonColor(stripeColor);
      drawExpander(node, cell, expanderColor, ctx);
      left = right + BLOCKS_VIEW_EXPANDER_MARGIN_RIGHT;
    }
    const headerTextBaseline = openGroupHeaderTopTextBaseline(node, cell);
    if (isComponent && node.representationData) {
      const sz = BLOCKS_VIEW_HEADER_INNER_HEIGHT;
      const representationX = left + sz / 2;
      drawRepresentationData([representationX, headerTextBaseline], sz);
    }

    if (mainLabel.length) {
      const [labelLeft] = labelRect(node, cell);
      const labelY = openGroupHeaderTopTextBaseline(node, cell);
      ctx.fillStyle = isComponent
        ? readableColor(nodeColor, colors.grey950, colors.white950)
        : colors.grey950;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = BLOCKS_VIEW_NODE_LABEL_FONT;

      mainLabel.forEach((line, ii) =>
        ctx!.fillText(
          line,
          labelLeft,
          labelY + ii * BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT
        )
      );
    }

    /* multilabels */
    const multiLabelsTop = stripeBottom + SMALL_SEP;
    const [bodyLeft, , bodyRight] = body;
    const multiLabelsBottom = drawMultilabels(
      bodyLeft,
      multiLabelsTop,
      bodyRight,
      false
    );

    if (subLabel.length) {
      const sz = BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT;
      const cursor: Vector = [bodyLeft, multiLabelsBottom + 0.5 * sz];

      ctx.fillStyle = colors.grey500;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = BLOCKS_VIEW_NODE_SUBLABEL_FONT;

      for (const line of subLabel) {
        ctx.fillText(line, ...cursor);

        cursor[1] += BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT;
      }
    }

    drawUrlIcon(node, cell, ctx);
    if (isHover) {
      drawHoverHighlight(ctx, bounds);
    }
    return;
  }
  const isCollapsedNonComponentGroup = nodeIsGroup && !open && !isComponent;
  if (isCollapsedNonComponentGroup) {
    drawCollapsedNonComponentGroup(node, cell, ctx);
    return;
  }

  /* node outline and fill */

  ctx.beginPath();
  roundRect(bounds, BLOCKS_VIEW_NODE_BORDERRADIUS);

  ctx.fillStyle = bg;
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = lineWidth;
  ctx.fill();
  ctx.stroke();

  if (nodeIsGroup && !open) {
    const expanderColor = readableButtonColor(bg);
    drawExpander(node, cell, expanderColor, ctx);

    // #region descendant count badge
    const badgeBounds = descendantCountBadgeRect(node, cell);
    const [badgeX, badgeY] = rectCenter(badgeBounds);
    drawDescendantCountBadge({
      fontSize: BLOCKS_VIEW_GROUP_BADGE_FONT_SIZE,
      x: badgeX,
      y: badgeY,
      descendantCount: node.descendantCount,
      disconnectedChildrenCount: 0,
      context: ctx,
      disconnectedNodesHighlighted: false,
      background: colors.grey900,
    });
    // #endregion
    // #region divider between header and content
    const headerBottom = collapsedGroupHeaderBottom(bounds);
    ctx.beginPath();
    ctx.moveTo(bounds[0], headerBottom);
    ctx.lineTo(bounds[2], headerBottom);
    ctx.strokeStyle = BLOCKS_VIEW_LEAF_BORDERCOLOR;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    // #endregion
  }

  /* representation data and label */
  const mainLabelHeight = mainLabel.length * BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT;
  /** the height of the node representation and all labels. based on this, all content will be vertically centered within the node bounds. */
  const contentHeight =
    BLOCKS_VIEW_LEAF_RD_INNERSIZE +
    (mainLabelHeight ? SMALL_SEP + mainLabelHeight : 0) +
    (multilabelHeight ? SMALL_SEP + multilabelHeight : 0);

  const [centerX, contentCenterY] = contentCenter(node, cell);
  let y = contentCenterY - contentHeight / 2;

  if (node.representationData) {
    const { shapeName, isImage } = node.representationData;
    const shouldDrawShape = !isImage && !isOpenGroup && shapeName;

    if (shouldDrawShape) {
      const { width: shapeWidth } = determineStyleSize(`#${shapeName}`);
      const shapeX = centerX - shapeWidth / 2;
      const shapeY = y;
      const shapeBounds: Rectangle = [
        shapeX,
        shapeY,
        shapeWidth,
        BLOCKS_VIEW_LEAF_RD_INNERSIZE,
      ];
      const shapeNameResolved =
        !shapeName || shapeName === 'none'
          ? componentTypeDefaultShape
          : shapeName;
      drawShape(ctx, nodeColor, shapeNameResolved, shapeBounds);
    } else {
      ctx.beginPath();
      const circleRadius = BLOCKS_VIEW_LEAF_RD_INNERSIZE / 2;
      const circleCenterY = y + circleRadius;
      drawRepresentationData(
        [centerX, circleCenterY],
        BLOCKS_VIEW_LEAF_RD_INNERSIZE - IMAGE_OUTLINE_WIDTH
      );
      // #region circular outline
      /** the radius of the circular outline, which is _inside_ the image bounds. because stroke is always middle-aligned in canvas rendering, subtract half the stroke to get the inside stroke alignment. */
      const outlineRadius = circleRadius - IMAGE_OUTLINE_WIDTH / 2;
      ctx.ellipse(
        centerX,
        circleCenterY,
        outlineRadius,
        outlineRadius,
        0,
        0,
        2 * Math.PI
      );
      ctx.lineWidth = IMAGE_OUTLINE_WIDTH;
      ctx.strokeStyle = nodeColor;
      ctx.stroke();
      ctx.lineWidth = lineWidth;
      // #endregion
    }

    y += BLOCKS_VIEW_LEAF_RD_INNERSIZE + SMALL_SEP;
  }

  if (mainLabel.length !== 0) {
    ctx.fillStyle = colors.grey900;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = BLOCKS_VIEW_NODE_LABEL_FONT;
    for (const line of mainLabel) {
      ctx.fillText(line, centerX, y + BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT / 2);
      y += BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT;
    }
  }

  if (subLabel.length !== 0) {
    /* sublabel */
  }

  drawMultilabels(body[0], y + SMALL_SEP, body[2], true);

  drawUrlIcon(node, cell, ctx);
  if (isHover) {
    drawHoverHighlight(ctx, bounds);
  }
};

export const renderDragGhost = (
  node: BlocksViewNode,
  context: CanvasRenderingContext
) => {
  if (node.cell) {
    drawNode(node, node.cell, 1.0, context);
  }
};
